import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { ALPHA, CONTROL_HEIGHT, FONT_SIZE, INK, LINE_HEIGHT } from "../../../theme/tokens";
import { TagChip } from "../../UIToolkit/Badges/TagChip";
import { GlyphButton } from "../../UIToolkit/Clickable/GlyphButton";
import { SystemTimePicker } from "../../UIToolkit/Inputs/SystemTimePicker";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { CONFIG_TABLE_BODY_CELL_SX, CONFIG_TABLE_BODY_ROW_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { type DayPartHoursSet, HOLIDAY_DAY_PARTS, type HolidayDayPart, HolidayPart, type JobDayPartHours, type PartHours } from "./holidayApi";
import { DAY_PART_LABEL_KEY, endsNextDay, formatClockTime, PART_SHORT_LABEL_KEY } from "./holidayFormat";
import { useSaveDayPartHours } from "./holidayQueries";

const CLOCK = "HH:mm";
/** The minutes a picker's arrows step by — day parts start on the quarter hour. */
const MINUTES_STEP = 15;

const toPicker = (clock: string | null): DateTime | null => (clock === null ? null : DateTime.fromFormat(clock, CLOCK));

type HoursCellProps = {
	dayPart: HolidayDayPart;
	/** "07:00" style, or null while a picker is cleared. */
	start: string | null;
	end: string | null;
	editing: boolean;
	/** The row is saving: the pickers hold still, so nothing typed now is lost when the save lands. */
	disabled: boolean;
	onChange: (edge: "start" | "end", clock: string | null) => void;
	dataTestId: string;
};

/** One day part of one job: "7:00 AM – 3:00 PM" to read, a From and a To picker while the row is edited. */
const renderHoursCell = (t: TFunction, { dayPart, start, end, editing, disabled, onChange, dataTestId }: HoursCellProps) => {
	const sameTime = start !== null && start === end;
	const nextDay = start !== null && end !== null && !sameTime && endsNextDay(start, end);
	const note = sameTime ? t("holidays.dayParts.sameTime", { part: t(DAY_PART_LABEL_KEY[dayPart]) }) : nextDay ? t("holidays.dayParts.endsNextDay") : "";
	const handle = (edge: "start" | "end") => (value: DateTime | null) => onChange(edge, value?.isValid ? value.toFormat(CLOCK) : null);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: editing ? 1.5 : 0.5 }} data-testid={dataTestId}>
			{editing ? (
				<>
					<SystemTimePicker
						value={toPicker(start)}
						onChange={handle("start")}
						label={t("holidays.dayParts.from")}
						minutesStep={MINUTES_STEP}
						disabled={disabled}
						dataTestId={`${dataTestId}-start`}
					/>
					<SystemTimePicker
						value={toPicker(end)}
						onChange={handle("end")}
						label={t("holidays.dayParts.to")}
						minutesStep={MINUTES_STEP}
						disabled={disabled}
						dataTestId={`${dataTestId}-end`}
					/>
				</>
			) : (
				// Wraps, so at the table's narrowest the "Next day" tag drops under the time instead of being cut to "Ne…".
				<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 0.75, rowGap: 0.5, minHeight: CONTROL_HEIGHT.MICRO }}>
					<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
						{start !== null && end !== null ? `${formatClockTime(start)} – ${formatClockTime(end)}` : "—"}
					</Typography>
					{/* An end before the start runs into the next morning — said once, beside the time it explains. */}
					{nextDay && <TagChip label={t(PART_SHORT_LABEL_KEY[HolidayPart.NextDay])} dataTestId={`${dataTestId}-next-day`} />}
				</Box>
			)}
			{editing && (
				<Typography
					aria-live="polite"
					// One line is always held for the note, so the row doesn't jump when one appears.
					sx={{ lineHeight: LINE_HEIGHT.SNUG, minHeight: `${LINE_HEIGHT.SNUG}em`, fontSize: FONT_SIZE.CAPTION, color: sameTime ? "error.main" : INK.MUTED }}
					data-testid={`${dataTestId}-note`}
				>
					{note}
				</Typography>
			)}
		</Box>
	);
};

type Draft = Record<HolidayDayPart, { start: string | null; end: string | null }>;

const validPart = ({ start, end }: Draft[HolidayDayPart]): PartHours | null => (start === null || end === null || start === end ? null : { start, end });

/** The draft as the server takes it, or null while any part is cleared or starts when it ends. */
const toValidHours = (draft: Draft): DayPartHoursSet | null => {
	const day = validPart(draft.day);
	const evening = validPart(draft.evening);
	const night = validPart(draft.night);
	return day && evening && night ? { day, evening, night } : null;
};

/** Top-aligned, so a row being edited (two pickers per part) keeps the job name level with the first picker. */
const CELL_SX = { py: 1.25, px: 1.5, verticalAlign: "top" } as const;

type Props = { job: JobDayPartHours; canEdit: boolean };

/** One job's Day, Evening and Night hours. Read at rest; the pencil edits the row, saved for that job alone. */
export const HolidayDayPartHoursRow: FC<Props> = ({ job, canEdit }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const [draft, setDraft] = useState<Draft | null>(null);
	const save = useSaveDayPartHours();
	const editing = draft !== null;
	const shown: Draft = draft ?? job.parts;
	const valid = toValidHours(shown);

	const setClock = (dayPart: HolidayDayPart, edge: "start" | "end", clock: string | null) =>
		setDraft((current) => {
			const base = current ?? job.parts;
			return { ...base, [dayPart]: { ...base[dayPart], [edge]: clock } };
		});

	const handleSave = () => {
		if (valid === null) return;
		save.mutate([{ jobId: job.jobId, parts: valid }], {
			onSuccess: () => {
				setDraft(null);
				showSnackbar(t("holidays.dayParts.saved", { job: job.name }), "success");
			},
		});
	};

	return (
		<TableRow
			// The row being edited takes a faint brand wash (and no hover wash), so it reads as the one open for changes.
			sx={editing ? (theme) => ({ backgroundColor: alpha(theme.palette.primary.main, ALPHA.HINT_TINT) }) : CONFIG_TABLE_BODY_ROW_SX}
			data-testid={`holiday-day-parts-row-${job.jobId}`}
			data-editing={editing}
		>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, CELL_SX]}>
				{/* Wraps: the job column is fixed, so a long name on a narrow screen would otherwise run into the Day hours. */}
				<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY, overflowWrap: "anywhere" }}>{job.name}</Typography>
			</TableCell>
			{HOLIDAY_DAY_PARTS.map((dayPart) => (
				<TableCell key={dayPart} sx={[CONFIG_TABLE_BODY_CELL_SX, CELL_SX]}>
					{renderHoursCell(t, {
						dayPart,
						start: shown[dayPart].start,
						end: shown[dayPart].end,
						editing,
						disabled: save.isPending,
						onChange: (edge, clock) => setClock(dayPart, edge, clock),
						dataTestId: `holiday-day-parts-${job.jobId}-${dayPart}`,
					})}
				</TableCell>
			))}
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { ...CELL_SX, width: 0 }]}>
				{canEdit && !editing && (
					<GlyphButton
						icon={<EditOutlinedIcon />}
						ariaLabel={t("holidays.dayParts.editAria", { job: job.name })}
						onClick={() => setDraft(job.parts)}
						dataTestId={`holiday-day-parts-edit-${job.jobId}`}
					/>
				)}
				{canEdit && editing && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "stretch" }}>
						<SystemButton
							buttonVariant="primary"
							size="small"
							onClick={handleSave}
							disabled={valid === null}
							loading={save.isPending}
							noFullWidthMobile
							data-testid={`holiday-day-parts-save-${job.jobId}`}
						>
							{t("save")}
						</SystemButton>
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={() => setDraft(null)}
							disabled={save.isPending}
							noFullWidthMobile
							data-testid={`holiday-day-parts-cancel-${job.jobId}`}
						>
							{t("cancel")}
						</SystemButton>
					</Box>
				)}
			</TableCell>
		</TableRow>
	);
};
